/**
 * Public ROI Calculator (landlord) — SEO-optimized, no auth required.
 *
 * Live-computed rental property investment analysis:
 *   - Cap rate, cash-on-cash return, break-even, total 5-year return
 *   - Includes mortgage amortization, vacancy allowance, expense ratios
 *
 * Target keywords: "landlord ROI calculator Canada", "rental property
 * cap rate calculator", "cash on cash return real estate".
 */
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PublicHeader from "@/components/layout/PublicHeader";
import SoftwareAppSchema from "@/components/seo/SoftwareAppSchema";
import PageJsonLd from "@/components/seo/PageJsonLd";
import { DirectAnswer, AuthorByline, SourceCitations, FreshnessBadge } from "@/components/seo/EEATBlocks";
import { usePageTitle } from "@/lib/seo";
import { Calculator, TrendingUp, DollarSign, Percent, ArrowRight, Sparkles, AlertCircle } from "lucide-react";

const money = (n) => new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(Math.round(Number.isFinite(n) ? n : 0));
const pct = (n) => `${(Number.isFinite(n) ? n : 0).toFixed(1)}%`;

// Safe numeric parsing — never returns NaN, tolerates commas & currency symbols.
const num = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const cleaned = String(v).replace(/[^\d.\-]/g, "");
  const parsed = parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

// Domain-safe clamps — prevent nonsense inputs like `downPaymentPct=25000%`.
const clampPct = (v, max = 100) => Math.max(0, Math.min(num(v), max));
const clampPrice = (v) => Math.max(0, Math.min(num(v), 1_000_000_000)); // $1B cap
const clampRate = (v) => Math.max(0, Math.min(num(v), 30)); // 30% annual max
const clampYears = (v) => Math.max(1, Math.min(num(v) || 25, 40));

// Fixed-rate mortgage monthly payment.
function mortgagePayment(principal, annualRate, years) {
  const p = num(principal);
  const r = num(annualRate) / 100 / 12;
  const n = num(years) * 12;
  if (p <= 0 || r <= 0 || n <= 0) return 0;
  const denom = 1 - Math.pow(1 + r, -n);
  if (denom === 0) return 0;
  return (p * r) / denom;
}

const REQUIRED_FIELDS = [
  { key: "purchasePrice", label: "purchase price" },
  { key: "downPaymentPct", label: "down payment %" },
  { key: "rate", label: "mortgage rate" },
  { key: "term", label: "amortization" },
  { key: "monthlyRent", label: "monthly rent" },
];

export default function RoiCalculator() {
  usePageTitle("Landlord ROI Calculator — Central Rentals Canada");

  const [inputs, setInputs] = useState({
    purchasePrice: "",
    downPaymentPct: "",
    rate: "",
    term: "",
    monthlyRent: "",
  });
  // "%" (default) or "$" — lets landlords enter the down payment in
  // whichever unit is on the tip of their tongue. Real Canadian buyers
  // usually think in dollars ("I have $50k saved") not percentages, so
  // this fixes the earlier UX bug where users typed the dollar amount
  // into a % field and got a bogus $0-loan result.
  const [downMode, setDownMode] = useState("%");
  const [shakeMissing, setShakeMissing] = useState(false);
  const firstInputRef = useRef(null);
  const resultsRef = useRef(null);

  const set = (k) => (e) => setInputs({ ...inputs, [k]: e.target.value });

  const reset = () => {
    setInputs({
      purchasePrice: "", downPaymentPct: "", rate: "", term: "",
      monthlyRent: "",
    });
    setDownMode("%");
  };

  // Which required fields are missing? Used for progress + shake highlight.
  // In "$" mode we validate the dollar down-payment field, in "%" mode the percent.
  const missing = REQUIRED_FIELDS.filter((f) => num(inputs[f.key]) <= 0);
  const ready = missing.length === 0;

  const r = useMemo(() => {
    // Clamp every input to a sane domain BEFORE math. Prevents the classic
    // "user typed 25000 in a % field" catastrophe that produces negative
    // loans and 3,000-year break-evens.
    const purchasePrice = clampPrice(inputs.purchasePrice);
    // In "$" mode the field holds a dollar amount; in "%" mode a percentage.
    // Convert whichever the user typed into an effective percentage capped
    // at 100 so the mortgage math has a well-defined domain.
    let downPaymentPct;
    if (downMode === "$") {
      const dollars = clampPrice(inputs.downPaymentPct);
      downPaymentPct = purchasePrice > 0 ? Math.min(100, (dollars / purchasePrice) * 100) : 0;
    } else {
      downPaymentPct = clampPct(inputs.downPaymentPct, 100);
    }
    const rateVal = clampRate(inputs.rate);
    const termVal = clampYears(inputs.term);
    const monthlyRentVal = clampPrice(inputs.monthlyRent);

    // ────────────────────────────────────────────────────────────────
    // CANADIAN MARKET DEFAULTS (2026) — baked in so the landlord gets
    // an ACCURATE ROI without needing to know every expense line.
    // Sources: CMHC Rental Market Report 2024, Ratesdotca 2026,
    // Statistics Canada Table 34-10-0133-01.
    // ────────────────────────────────────────────────────────────────
    const PROPERTY_TAX_RATE = 0.01;     // 1.0% of assessed value/yr (national avg)
    const INSURANCE_RATE = 0.0035;      // 0.35% of assessed value/yr (landlord policy)
    const MAINTENANCE_PCT_RENT = 0.08;  // 8% of gross rent (national reserve avg)
    const VACANCY_PCT_RENT = 0.03;      // 3% (2024 CMHC national vacancy)
    const APPRECIATION_PCT = 0.03;      // 3%/yr long-run Canadian home appreciation

    const downPayment = purchasePrice * (downPaymentPct / 100);
    const loanAmount = Math.max(0, purchasePrice - downPayment);
    const monthlyMortgage = mortgagePayment(loanAmount, rateVal, termVal);
    const annualRent = monthlyRentVal * 12;
    const vacancyLoss = annualRent * VACANCY_PCT_RENT;
    const effectiveRent = annualRent - vacancyLoss;
    const propertyTax = purchasePrice * PROPERTY_TAX_RATE;
    const insurance = purchasePrice * INSURANCE_RATE;
    const maintenance = annualRent * MAINTENANCE_PCT_RENT;
    const operatingExpenses = maintenance + propertyTax + insurance;
    const noi = effectiveRent - operatingExpenses;
    const annualMortgage = monthlyMortgage * 12;
    const annualCashFlow = noi - annualMortgage;
    const monthlyCashFlow = annualCashFlow / 12;
    const closingCostsEst = purchasePrice * 0.015;
    const totalInvested = downPayment + closingCostsEst;

    const capRate = purchasePrice > 0 ? (noi / purchasePrice) * 100 : 0;
    const cocReturn = totalInvested > 0 ? (annualCashFlow / totalInvested) * 100 : 0;
    const debtCoverage = annualMortgage > 0 ? noi / annualMortgage : 0;

    const futureValue = purchasePrice * Math.pow(1 + APPRECIATION_PCT, 5);
    const equityGain = futureValue - purchasePrice;
    const cashFlow5y = annualCashFlow * 5;
    const totalReturn5y = equityGain + cashFlow5y;
    const roi5y = totalInvested > 0 ? (totalReturn5y / totalInvested) * 100 : 0;
    const breakEvenMonths = monthlyCashFlow > 0 ? Math.ceil(totalInvested / monthlyCashFlow) : Infinity;

    // Sanity flags — surfaced to the UI so we never SILENTLY show wrong
    // numbers. Adding a new bound? Add its warning here.
    const warnings = [];
    if (num(inputs.downPaymentPct) > 100)
      warnings.push("Down payment % capped at 100% (you entered " + inputs.downPaymentPct + "%)");
    if (num(inputs.rate) > 30)
      warnings.push("Mortgage rate capped at 30% (you entered " + inputs.rate + "%)");

    return {
      downPayment, loanAmount, monthlyMortgage, annualCashFlow, monthlyCashFlow,
      noi, operatingExpenses, propertyTax, insurance, maintenance, vacancyLoss,
      capRate, cocReturn, debtCoverage,
      totalInvested, roi5y, futureValue, breakEvenMonths, warnings,
    };
  }, [inputs]);

  const positive = r.monthlyCashFlow >= 0;

  // Hero CTA: smooth-scroll to calc + focus first input for immediate typing.
  const jumpToCalculator = (e) => {
    e?.preventDefault?.();
    document.getElementById("calculator")?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => firstInputRef.current?.focus(), 350);
  };

  // Bottom CTA: scroll to results OR shake missing fields.
  const calculateAction = () => {
    if (!ready) {
      setShakeMissing(true);
      setTimeout(() => setShakeMissing(false), 800);
      // Focus the first missing required field.
      const first = missing[0]?.key;
      if (first) {
        const el = document.querySelector(`[data-input-key="${first}"]`);
        el?.focus?.();
        el?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      }
      return;
    }
    resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SoftwareAppSchema />
      <PageJsonLd
        breadcrumbs={[
          { name: "Home", url: "https://centralrentals.ca/" },
          { name: "Tools", url: "https://centralrentals.ca/tools" },
          { name: "ROI Calculator", url: "https://centralrentals.ca/tools/roi-calculator" },
        ]}
        howTo={{
          name: "How to calculate ROI on a Canadian rental property",
          totalTime: "PT2M",
          steps: [
            "Enter the purchase price and down payment percentage.",
            "Enter the mortgage rate and amortization period in years.",
            "Enter the expected monthly rent.",
            "Add annual property tax, insurance, and monthly costs (condo fees, utilities).",
            "Review the calculated cap rate, cash-on-cash return, and 5-year ROI.",
          ],
        }}
        faq={[
          {
            q: "How do you calculate ROI on a Canadian rental property?",
            a: "Return on Investment (ROI) is calculated using three metrics: cap rate (Net Operating Income ÷ purchase price × 100), cash-on-cash return (annual cash flow ÷ total cash invested × 100), and total return (cash flow + property appreciation ÷ cash invested × 100).",
          },
          {
            q: "What's a good cap rate for Canadian rentals in 2026?",
            a: "A good cap rate depends on the market: Toronto and Vancouver core condos typically show 3-4%, while secondary Ontario cities and most of Alberta and Saskatchewan show 5-7%.",
          },
          {
            q: "Is this ROI calculator free?",
            a: "Yes. The Central Rentals Canada Landlord ROI Calculator is 100% free, no signup required, and computes results instantly in your browser with no data sent to our servers.",
          },
        ]}
        webApp={{
          name: "Central Rentals Landlord ROI Calculator",
          url: "https://centralrentals.ca/tools/roi-calculator",
          description: "Free landlord ROI calculator for Canadian rental properties. Compute cap rate, cash-on-cash return, and 5-year total return in 60 seconds.",
        }}
      />
      <PublicHeader />

      {/* HERO with above-fold interactive CTA */}
      <section className="relative bg-gradient-to-b from-white to-slate-50 border-b border-slate-100 pt-12 md:pt-20 pb-8">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
              <Calculator className="w-3.5 h-3.5"/> Free landlord tool
            </div>
            <h1 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-slate-900 leading-[1.05]" data-testid="roi-hero-title">
              Landlord ROI Calculator for Canadian rental properties
            </h1>
            <FreshnessBadge updatedAt="2026-07-31" className="mt-4"/>
            <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl">
              Calculate cap rate, cash-on-cash return, and 5-year ROI on any Canadian rental property in 60 seconds. Built by the team behind Central Rentals Canada — the smart tenant screening and automated rent roll reconciliation platform trusted by landlords across Canada.
            </p>
            <DirectAnswer
              question="How do you calculate ROI on a Canadian rental property?"
              answer="Divide the annual net operating income by the total cash invested (down payment + closing costs) and multiply by 100. In Canada in 2026, a healthy rental ROI is 5-8% cash-on-cash, with a cap rate of 4-6% for major cities and 6-8% for secondary markets."
              source="Central Rentals Canada · CMHC RMS 2024"
              className="mt-6 max-w-2xl"
            />
            <AuthorByline
              name="Central Rentals Editorial Team"
              credentials="Canadian rental market research desk"
              reviewedAt="2026-07-31"
              className="max-w-2xl"
            />
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={jumpToCalculator}
                data-testid="roi-hero-cta"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 active:bg-slate-700 text-white text-sm font-bold cursor-pointer touch-manipulation"
              >
                <Sparkles className="w-4 h-4"/> Calculate ROI
              </button>
              <Link to="/signup?role=landlord" data-testid="roi-hero-demo" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white border border-slate-300 hover:border-slate-400 text-slate-800 text-sm font-bold">
                See it in action <ArrowRight className="w-4 h-4"/>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CALCULATOR */}
      <section id="calculator" className="py-14 scroll-mt-20">
        <div className="max-w-6xl mx-auto px-5 md:px-8 grid lg:grid-cols-5 gap-8">
          {/* Inputs */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6" data-testid="roi-inputs">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-bold tracking-tight text-slate-900">Your inputs</h2>
              <button
                type="button"
                onClick={reset}
                data-testid="roi-reset-btn"
                className="text-xs font-semibold text-slate-500 hover:text-slate-800 underline underline-offset-4"
              >
                Clear
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-500">Enter your numbers below — every field is <span className="text-rose-600 font-bold">required</span>. We use Canadian market averages for taxes, insurance, maintenance and vacancy so you get an accurate ROI without a spreadsheet.</p>
            <div className="mt-4 space-y-4">
              <Field label="Purchase price (CAD)" required value={inputs.purchasePrice} onChange={set("purchasePrice")} placeholder="e.g. 550000" testid="input-price" inputKey="purchasePrice" shake={shakeMissing && num(inputs.purchasePrice) <= 0} inputRef={firstInputRef}/>
              <DownPaymentField
                mode={downMode}
                onModeChange={(next) => {
                  // Convert the current value when switching modes so the user's
                  // typed number keeps its economic meaning (e.g. 20% ↔ $110k
                  // on a $550k purchase). Falls back to a raw copy when the
                  // purchase price hasn't been entered yet.
                  const currentVal = num(inputs.downPaymentPct);
                  const price = num(inputs.purchasePrice);
                  let converted = inputs.downPaymentPct;
                  if (price > 0 && currentVal > 0) {
                    if (downMode === "%" && next === "$") {
                      converted = Math.round(price * (Math.min(currentVal, 100) / 100)).toString();
                    } else if (downMode === "$" && next === "%") {
                      converted = Math.min(100, Math.round((currentVal / price) * 1000) / 10).toString();
                    }
                  }
                  setInputs({ ...inputs, downPaymentPct: converted });
                  setDownMode(next);
                }}
                value={inputs.downPaymentPct}
                onChange={set("downPaymentPct")}
                purchasePrice={num(inputs.purchasePrice)}
                shake={shakeMissing && num(inputs.downPaymentPct) <= 0}
              />
              <Field label="Mortgage rate (% annual)" required value={inputs.rate} onChange={set("rate")} step={0.01} placeholder="e.g. 4.99 (2026 avg 5-yr fixed)" testid="input-rate" inputKey="rate" shake={shakeMissing && num(inputs.rate) <= 0}/>
              <Field label="Amortization (years)" required value={inputs.term} onChange={set("term")} placeholder="e.g. 25" testid="input-term" inputKey="term" shake={shakeMissing && num(inputs.term) <= 0}/>
              <Field label="Monthly rent (CAD)" required value={inputs.monthlyRent} onChange={set("monthlyRent")} placeholder="e.g. 2800" testid="input-rent" inputKey="monthlyRent" shake={shakeMissing && num(inputs.monthlyRent) <= 0}/>
            </div>

            <div className="mt-5 rounded-xl bg-emerald-50 border border-emerald-200 p-3.5 text-[11px] text-emerald-900 leading-relaxed" data-testid="roi-defaults-note">
              <div className="font-bold text-emerald-800 uppercase tracking-wider text-[10px] mb-1.5">Canadian market defaults applied</div>
              Property tax <b>1.0%</b>/yr · Landlord insurance <b>0.35%</b>/yr · Maintenance reserve <b>8%</b> of gross rent · Vacancy <b>3%</b> (CMHC 2024) · Home appreciation <b>3%</b>/yr (long-run CA average).
            </div>

            {/* Explicit "Calculate" button — feels like a real action on touch */}
            <button
              type="button"
              onClick={calculateAction}
              data-testid="roi-calculate-btn"
              className={`mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl text-sm font-bold cursor-pointer touch-manipulation transition-colors ${ready ? "bg-emerald-500 hover:bg-emerald-400 active:bg-emerald-600 text-slate-900" : "bg-slate-900 hover:bg-slate-800 active:bg-slate-700 text-white"}`}
            >
              <Calculator className="w-4 h-4"/>
              {ready ? "See my ROI results" : `Calculate my ROI (${5 - missing.length}/5 required fields)`}
            </button>
            {!ready && (
              <div data-testid="roi-missing-hint" className="mt-2 flex items-start gap-2 text-xs text-slate-500">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-none text-amber-500"/>
                <span>Add: {missing.map((m) => m.label).join(", ")}</span>
              </div>
            )}
          </div>

          {/* Results */}
          <div ref={resultsRef} className="lg:col-span-3 space-y-4 scroll-mt-20" data-testid="roi-results">
            {!ready ? (
              <div className="rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center bg-white" data-testid="roi-empty">
                <Calculator className="w-8 h-8 mx-auto text-slate-300"/>
                <p className="mt-3 font-display text-lg font-bold text-slate-800">Enter your numbers to see results</p>
                <p className="mt-1 text-sm text-slate-500 max-w-sm mx-auto">Fill in purchase price, down payment %, mortgage rate, amortization, and monthly rent. We apply Canadian market averages for the rest.</p>
                {/* Progress bar */}
                <div className="mt-4 max-w-xs mx-auto">
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 transition-all duration-300" style={{ width: `${((5 - missing.length) / 5) * 100}%` }}/>
                  </div>
                  <div className="mt-1.5 text-xs text-slate-400 tabular-nums">{5 - missing.length} of 5 required fields</div>
                </div>
              </div>
            ) : (
            <>
            <div className={`rounded-2xl p-6 ${positive ? "bg-emerald-50 border border-emerald-200" : "bg-rose-50 border border-rose-200"}`}>
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">
                <DollarSign className="w-3.5 h-3.5"/> Monthly cash flow
              </div>
              <div className={`mt-1 font-display text-5xl font-black tracking-tighter tabular-nums ${positive ? "text-emerald-800" : "text-rose-800"}`} data-testid="result-monthly-cf">
                {money(r.monthlyCashFlow)}
              </div>
              <div className="text-sm text-slate-600 mt-1">
                {positive ? "Positive cash flow — this property pays you every month." : "Negative cash flow — you'd top up out of pocket monthly."}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <MetricCard icon={Percent} label="Cap rate" value={pct(r.capRate)} testid="result-cap"/>
              <MetricCard icon={TrendingUp} label="Cash-on-cash return" value={pct(r.cocReturn)} testid="result-coc"/>
              <MetricCard icon={Calculator} label="5-year ROI (inc. appreciation)" value={pct(r.roi5y)} testid="result-roi5y"/>
              <MetricCard
                icon={DollarSign}
                label="Debt coverage ratio"
                value={
                  r.loanAmount <= 0
                    ? "N/A (no debt)"
                    : Number.isFinite(r.debtCoverage)
                      ? r.debtCoverage.toFixed(2)
                      : "0.00"
                }
                testid="result-dcr"
              />
            </div>

            {r.warnings && r.warnings.length > 0 && (
              <div
                className="rounded-2xl bg-amber-50 border border-amber-300 p-4 text-xs text-amber-900 space-y-1"
                data-testid="roi-validation-warnings"
              >
                <strong className="flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5" /> Input adjustments applied:
                </strong>
                <ul className="list-disc pl-5">
                  {r.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="roi-breakdown">
              <h3 className="font-display font-bold text-slate-900 text-sm mb-3">Breakdown</h3>
              <dl className="text-sm space-y-2">
                <Row label="Down payment" value={money(r.downPayment)}/>
                <Row label="Loan amount" value={money(r.loanAmount)} hint={r.loanAmount === 0 ? "All-cash purchase (100% down)" : null}/>
                <Row label="Monthly mortgage payment" value={money(r.monthlyMortgage)} hint={r.monthlyMortgage === 0 ? "No mortgage — nothing to pay" : null}/>
                <Row label="Vacancy loss (3% of rent)" value={money(r.vacancyLoss)}/>
                <Row label="Property tax (1.0% of value/yr)" value={money(r.propertyTax)}/>
                <Row label="Insurance (0.35% of value/yr)" value={money(r.insurance)}/>
                <Row label="Maintenance reserve (8% of rent)" value={money(r.maintenance)}/>
                <Row label="Net operating income (annual)" value={money(r.noi)}/>
                <Row label="Total operating expenses (annual)" value={money(r.operatingExpenses)}/>
                <Row label="Total cash invested (down + closing)" value={money(r.totalInvested)}/>
                <Row label="Property value after 5 years (3%/yr)" value={money(r.futureValue)}/>
                <Row label="Break-even (time to recover cash invested)" value={
                  r.breakEvenMonths === Infinity
                    ? "Never (negative cash flow)"
                    : r.breakEvenMonths > 600
                      ? `${Math.round(r.breakEvenMonths / 12)} yrs (long payback)`
                      : r.breakEvenMonths > 24
                        ? `${Math.round(r.breakEvenMonths / 12 * 10) / 10} yrs`
                        : `${r.breakEvenMonths} months`
                }/>
              </dl>
            </div>

            <div className="rounded-2xl bg-slate-900 text-white p-5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-400">Ready to buy?</div>
                  <h3 className="mt-1 font-display font-bold text-lg tracking-tight">Manage it with Central Rentals</h3>
                  <p className="mt-1 text-sm text-slate-300">Automated rent roll reconciliation, smart tenant screening, provincial legal notices — from $35 CAD/month.</p>
                </div>
                <Link to="/signup?role=landlord" data-testid="roi-results-cta" className="flex-none inline-flex items-center gap-1 px-4 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-900 text-sm font-bold">
                  Choose a plan <ArrowRight className="w-4 h-4"/>
                </Link>
              </div>
            </div>
            </>
            )}
          </div>
        </div>
      </section>

      {/* Direct Answer Block */}
      <section className="py-14 bg-white border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-700">Direct answer</div>
          <h2 className="mt-2 font-display text-3xl md:text-4xl font-bold tracking-tighter text-slate-900 leading-tight">
            How do you calculate ROI on a Canadian rental property?
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Return on Investment (ROI) on a Canadian rental property is calculated using three metrics: <strong>cap rate</strong> (Net Operating Income ÷ purchase price × 100), <strong>cash-on-cash return</strong> (annual cash flow ÷ total cash invested × 100), and <strong>total return</strong> (cash flow + property appreciation ÷ cash invested × 100). Central Rentals Canada&rsquo;s free calculator above computes all three in real time with your inputs.
          </p>

          <div className="mt-8 text-xs uppercase tracking-[0.2em] font-bold text-emerald-700">Direct answer</div>
          <h2 className="mt-2 font-display text-3xl md:text-4xl font-bold tracking-tighter text-slate-900 leading-tight">
            What&rsquo;s a good cap rate for Canadian rentals in 2026?
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            A &ldquo;good&rdquo; cap rate for a Canadian rental in 2026 depends on the market: Toronto and Vancouver core condos typically show 3-4%, while secondary Ontario cities (Windsor, Kingston, Kitchener-Waterloo) and most of Alberta and Saskatchewan show 5-7%. A cap rate below the 5-year GoC bond yield generally means the investment is speculative — you&rsquo;re betting on appreciation more than income.
          </p>

          <SourceCitations
            sources={[
              { name: "Rental Market Survey (RMS) 2024", publisher: "Canada Mortgage and Housing Corporation (CMHC)", year: 2024, url: "https://www.cmhc-schl.gc.ca/professionals/housing-markets-data-and-research/housing-research/rental-market-reports-canada" },
              { name: "Ontario 2026 Rent Increase Guideline", publisher: "Government of Ontario", year: 2026, url: "https://www.ontario.ca/page/rent-increase-guideline" },
              { name: "BC Residential Tenancy Branch — Rent increases", publisher: "Government of British Columbia", year: 2026, url: "https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies/during-a-tenancy/rent-increases" },
              { name: "Statistics Canada Table 34-10-0133-01", publisher: "Statistics Canada", year: 2024, url: "https://www150.statcan.gc.ca/t1/tbl1/en/tv.action?pid=3410013301" },
            ]}
          />
        </div>
      </section>
    </div>
  );
}

function DownPaymentField({ mode, onModeChange, value, onChange, purchasePrice, shake }) {
  const raw = num(value);
  // Live-derived hint: show the "other unit" so users never have to guess
  // what their input means. This is the fix for the "typed $ into a % field"
  // UX bug that produced $0 loans + $0 mortgages.
  let hint = "Typical Canadian down payment: 5% – 25%";
  if (raw > 0 && purchasePrice > 0) {
    if (mode === "%") {
      const capped = Math.min(raw, 100);
      const dollars = Math.round(purchasePrice * (capped / 100));
      hint = `= ${money(dollars)} CAD`;
      if (raw > 100) hint += " · capped at 100%";
    } else {
      const pct = (raw / purchasePrice) * 100;
      hint = `= ${pct.toFixed(1)}% of purchase price`;
      if (raw > purchasePrice) hint += " · capped at 100%";
    }
  } else if (raw > 0 && mode === "$") {
    hint = "Enter purchase price above to see the % equivalent";
  }
  return (
    <div data-testid="input-down-wrapper">
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold text-slate-600">
          Down payment <span className="text-rose-600 ml-0.5" aria-hidden="true">*</span>
        </label>
        <div className="inline-flex text-[11px] font-bold rounded-md overflow-hidden border border-slate-200" role="group" aria-label="Down payment unit">
          <button
            type="button"
            data-testid="input-down-mode-pct"
            onClick={() => onModeChange("%")}
            className={`px-2.5 py-1 transition-colors ${mode === "%" ? "bg-emerald-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
          >
            %
          </button>
          <button
            type="button"
            data-testid="input-down-mode-dollar"
            onClick={() => onModeChange("$")}
            className={`px-2.5 py-1 border-l border-slate-200 transition-colors ${mode === "$" ? "bg-emerald-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
          >
            $
          </button>
        </div>
      </div>
      <div className="relative mt-1">
        {mode === "$" && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 pointer-events-none select-none">$</span>
        )}
        <input
          type="number"
          inputMode="decimal"
          value={value}
          onChange={onChange}
          placeholder={mode === "%" ? "e.g. 20" : "e.g. 110000"}
          step={mode === "%" ? 0.5 : 1000}
          min={0}
          max={mode === "%" ? 100 : undefined}
          data-testid="input-down"
          data-input-key="downPaymentPct"
          aria-required="true"
          className={`w-full rounded-lg border ${mode === "$" ? "pl-6 pr-8" : "pl-3 pr-8"} py-2 text-sm tabular-nums outline-none placeholder:text-slate-300 transition-colors ${shake ? "border-rose-400 ring-1 ring-rose-300 animate-pulse bg-rose-50" : "border-slate-200 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"}`}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none select-none">
          {mode === "%" ? "%" : "CAD"}
        </span>
      </div>
      <div className="mt-1 text-[11px] text-slate-500" data-testid="input-down-hint">{hint}</div>
    </div>
  );
}

function Field({ label, required, value, onChange, step, max, placeholder, testid, inputKey, shake, inputRef }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600">
        {label}{required && <span className="text-rose-600 ml-0.5" aria-hidden="true">*</span>}
      </label>
      <input
        ref={inputRef}
        type="number"
        inputMode="decimal"
        value={value}
        onChange={onChange}
        placeholder={placeholder || ""}
        step={step || 1}
        min={0}
        max={max}
        data-testid={testid}
        data-input-key={inputKey}
        aria-required={required || undefined}
        className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm tabular-nums outline-none placeholder:text-slate-300 transition-colors ${shake ? "border-rose-400 ring-1 ring-rose-300 animate-pulse bg-rose-50" : "border-slate-200 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"}`}
      />
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4">
      <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider">
        <Icon className="w-3.5 h-3.5"/> {label}
      </div>
      <div className="mt-1 text-2xl font-bold tracking-tight text-slate-900 tabular-nums" data-testid={testid}>{value}</div>
    </div>
  );
}

function Row({ label, value, hint }) {
  return (
    <div className="flex justify-between gap-3 py-1 border-b border-dashed border-slate-100 last:border-0">
      <dt className="text-slate-600">
        {label}
        {hint && <div className="text-xs text-slate-400 font-normal mt-0.5">{hint}</div>}
      </dt>
      <dd className="font-semibold text-slate-900 tabular-nums">{value}</dd>
    </div>
  );
}
